// Normalizes a string for accent-insensitive, case-insensitive search.
// Strips diacritics (e.g. "Café" -> "cafe") so a plain ASCII query matches
// accented names and vice-versa. Client-side only — does not affect API calls.
export const normalizeForSearch = (value) => {
  if (!value) return '';
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
};

// Latin letters NFD cannot split into "base letter + combining accent", so each
// needs an explicit ASCII equivalent (RAT-1908: "Cafe Derick îî òø ùũ äæ").
const ASCII_LETTER_EQUIVALENTS = {
  æ: 'ae',
  Æ: 'AE',
  œ: 'oe',
  Œ: 'OE',
  ø: 'o',
  Ø: 'O',
  ß: 'ss',
  ð: 'd',
  Ð: 'D',
  đ: 'd',
  Đ: 'D',
  ł: 'l',
  Ł: 'L',
  þ: 'th',
  Þ: 'TH',
  ħ: 'h',
  Ħ: 'H',
  ı: 'i',
  ŋ: 'n',
  Ŋ: 'N',
  ĸ: 'k',
};

// RAT-1908: download file names must be plain ASCII. Accented letters lose their
// diacritic ("é" -> "e", "ø" -> "o", "æ" -> "ae") and every other character
// outside [A-Za-z0-9._-] becomes a single dash. Mirrors toAsciiFileName() in the
// backend (src/utils/commonFormat.util.ts) - keep the two in step.
//
// Needed here because these downloads build the Blob client-side, so the saved
// name comes from the anchor's download attribute, not from Content-Disposition:
//   "INV-NL2026.000004255-Y2026M1-M6-SP-Cafe Derick îî òø ùũ äæ"
//     -> "INV-NL2026.000004255-Y2026M1-M6-SP-Cafe-Derick-ii-oo-uu-aae"
// Dots inside the invoice number are kept, so the file name still matches the
// number printed on the PDF; only repeated dots collapse.
export const toAsciiFileName = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7E]/g, (ch) => ASCII_LETTER_EQUIVALENTS[ch] ?? ch)
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/\.{2,}/g, '.')
    .replace(/-+\./g, '.')
    .replace(/^[-.]+|[-.]+$/g, '');

export const formatDate = (isoDateString) => {
  if (!isoDateString) return '';

  const date = new Date(isoDateString);
  const day = String(date.getDate()).padStart(2, '0'); // Ensure 2-digit day
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const year = date.getFullYear();

  // return `${year}-${month}-${day}`;
  return `${day}-${month}-${year}`;
};

// RAT-1865: same filter shape as the Merchant list API params (minus pagination),
// so a "Current filter results" export matches exactly what is on screen. Empty
// filters are omitted rather than sent as blanks.
export const buildMerchantExportFilters = (params, search) => {
  const filters = { search, isHidden: params.archivedView === 'true' };
  [
    'inMaintenance',
    'isTemporarilyClosed',
    'isComingSoon',
    'legalInfoStatus',
    // RAT-1690: the label filter counts too, or an export of "current results"
    // would silently ignore it and return more rows than the screen shows.
    'labelIds',
  ].forEach((key) => {
    if (params[key] !== '' && params[key] !== undefined) {
      filters[key] = params[key];
    }
  });
  return filters;
};

/**
 * RAT-1677: the station list's filter set, minus pagination.
 *
 * This is the single definition of "what the station list is currently showing" -
 * `StationList` spreads it into its own list request as well as handing it to the
 * export, so a "Current filter results" download cannot drift from the screen.
 *
 * `sortKey` / `sortBy` are always sent, matching the list request; the rest are
 * omitted when unset. Every value arrives from the URL as a string, so `'0'` (the
 * "0% Empty" occupancy option) is kept rather than dropped as falsy.
 */
export const buildStationExportFilters = (params, search) => {
  const filters = { search, sortKey: params.sortKey, sortBy: params.sortBy };
  [
    'network',
    'status',
    'posStatus',
    'condition',
    'merchantStatus',
    'stockOccupancy',
    'availablePBsOccupancy',
    'offlineMinutesFilter',
    'region',
  ].forEach((key) => {
    if (params[key] !== '' && params[key] !== undefined) {
      filters[key] = params[key];
    }
  });

  const excludeMerchant = params.excludeMerchant
    ? params.excludeMerchant.split(',').filter(Boolean)
    : [];
  if (excludeMerchant.length) {
    filters.excludeMerchant = excludeMerchant;
  }
  if (params.excludeUnassigned === '1') {
    filters.excludeUnassigned = true;
  }

  return filters;
};

export const formatCurrency = (amount, currency = 'EUR') => {
  // eslint-disable-next-line no-undef
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};
